import { useEffect, useState, type ReactNode } from 'react';
import { Spinner } from './Spinner';

/**
 * Blocking confirm. For destructive actions pass `requireText` (e.g. the inbox
 * name) — the confirm button stays disabled until it is typed exactly. Pass
 * `blocked` when the body holds a decision of its own that must be made first.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger = false,
  requireText,
  blocked: undecided = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  requireText?: string;
  blocked?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const blocked = undecided || (requireText !== undefined && typed !== requireText);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        <div className="dialog-body">
          {body}
          {requireText !== undefined && (
            <label className="field">
              <span className="field-label">
                Type <strong>{requireText}</strong> to confirm
              </span>
              <input
                className="input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                disabled={busy}
              />
            </label>
          )}
        </div>
        <div className="dialog-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={blocked || busy}
          >
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
